import {
  cleanAndJoin,
  extractResultUrls,
  filterBySignStatus,
  findRelatedResources,
  processDocumentBaseData,
  translateCategory,
} from "@/utils/fhirDocumentUtils";
import { DocumentReference } from "fhir/r5";

/**
 * Hook para processar documentos FHIR
 * Centraliza lógica de transformação de dados para diferentes tipos de documentos
 */

/**
 * Processa dados de prescrições médicas (MedicalNotes)
 */
export function processMedicalNotes(
  medicalNoteData: any[],
  medicationRequest: any[],
  sign: boolean,
) {
  // Filtra documentos baseado no status de assinatura
  const filteredNotes = filterBySignStatus(medicalNoteData, sign);

  // Processa cada documento
  return filteredNotes.map(
    (el: fhir5.BundleEntry<DocumentReference | any>, index: number) => {
      // Busca MedicationRequest relacionado para obter descrição dos medicamentos
      const relatedMedications = findRelatedResources(
        el?.resource,
        medicationRequest,
      );

      // Extrai descrição dos medicamentos
      const description =
        relatedMedications
          ?.map(
            (medicationRequestPrescription: any) =>
              medicationRequestPrescription?.resource?.medication?.concept
                ?.text,
          )
          ?.join(" , ") || "";

      // Processa dados base do documento
      const baseData = processDocumentBaseData(el, sign);

      return {
        ...baseData,
        description,
      };
    },
  );
}

/**
 * Processa dados de solicitações de exame (Solicitations)
 */
export function processSolicitations(
  solicitationData: any[],
  serviceRequest: any[],
  resultsData: any[],
  sign: boolean,
) {
  // Filtra apenas DocumentReference com categoria "Solicitação"
  const documentReferences =
    solicitationData
      ?.filter((el: any) => el?.resource?.resourceType === "DocumentReference")
      ?.filter((e: any) => {
        const isSigned =
          e?.resource?.securityLabel?.[0]?.coding?.[0]?.code ===
          "ProofOfOrigin";
        return sign ? isSigned : !isSigned;
      })
      ?.filter(
        (filterEl: any) =>
          filterEl?.resource?.category?.[0]?.coding?.[0]?.display ===
          "Solicitação",
      ) || [];

  // Processa cada solicitação
  return documentReferences.map(
    (el: fhir5.BundleEntry<DocumentReference | any>, index: number) => {
      // Busca ServiceRequest relacionado
      const relatedServices = findRelatedResources(
        el?.resource,
        serviceRequest,
      );

      // Processa categoria dos ServiceRequest relacionados
      const auxCategory = cleanAndJoin(
        relatedServices
          ?.filter(
            (item: any) => item !== null && item !== undefined && item !== "",
          )
          ?.map((el: any) => el?.resource?.category?.[0]?.coding?.[0].display),
      );

      // Busca resultados relacionados aos ServiceRequest
      const auxResults = resultsData?.[0]?.entry?.filter((result: any) => {
        const findRef = relatedServices?.find(
          (service: any) =>
            service?.resource?.id ===
            result?.resource?.basedOn
              ?.find((bas: any) => bas.reference.includes("ServiceRequest"))
              ?.reference?.split("/")?.[1],
        );
        return findRef;
      });

      // Extrai URLs dos resultados
      const resultUrls = extractResultUrls(auxResults);
      const resultUrl = resultUrls.length > 0 ? resultUrls[0] : "";

      // Extrai o tipo do documento
      const auxType = el?.resource?.type?.coding?.[0]?.display || "-";

      // Processa dados base do documento
      const baseData = processDocumentBaseData(el, sign);

      // Extrai descrição dos serviços
      const description =
        `${relatedServices
          ?.map(
            (serviceRequest: any) =>
              serviceRequest?.resource?.code?.concept?.coding?.[0]?.display,
          )
          ?.join(", ")}` || "";

      // Extrai practitioner identifier separadamente
      const practitionerId =
        el?.resource?.contained?.find(
          (contained: any) => contained?.resourceType === "Practitioner",
        )?.identifier?.[0]?.value || "";

      return {
        id: index,
        ...baseData,
        type: auxType,
        fullServiceResource: relatedServices,
        category: translateCategory(auxCategory),
        description,
        practitionerId,
        auxResults,
        resultUrls,
        resultUrl,
      };
    },
  );
}

/**
 * Processa dados de relatórios e atestados (NoteReport)
 */
export function processNoteReports(noteReportData: any[], sign: boolean) {
  // Filtra documentos baseado no status de assinatura
  const filteredReports = filterBySignStatus(noteReportData, sign);

  // Processa cada relatório
  return filteredReports.map(
    (el: fhir5.BundleEntry<DocumentReference | any>, index: number) => {
      return processDocumentBaseData(el, sign);
    },
  );
}

/**
 * Filtra documentos por categoria específica
 */
export function filterByCategory(data: any[], category: string): any[] {
  if (!data) return [];

  return data.filter(
    (doc: any) =>
      doc?.resource?.resourceType === "DocumentReference" &&
      doc?.resource?.category?.[0]?.coding?.[0]?.display === category,
  );
}
